/* eslint-disable react-refresh/only-export-components */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { fetchFileStatistics, fetchFilesList } from '@/api/files';
import type { ReplaceFilePayload } from '@/api/files/types';
import { queryClient } from '@/lib/utils/queryClient';
import { FileDetailsSheet } from '../../-components/FileDetailsSheet';
import { FileStatsStrip } from '../../-components/FileStatsStrip';
import { FilesTable } from '../../-components/FilesTable';
import { useDeleteFiles } from '../../-hooks/useDeleteFiles';
import { useFileDetail } from '../../-hooks/useFileDetail';
import {
  fileStatsKeys,
  useFileStatistics,
} from '../../-hooks/useFileStatistics';
import { filesListKeys, useFilesList } from '../../-hooks/useFilesList';
import { useReplaceFile } from '../../-hooks/useReplaceFile';
import { filesListSearch } from '../../-types';

export const Route = createFileRoute('/(client)/files/(pages)/(list)/')({
  validateSearch: filesListSearch,
  loaderDeps: ({ search }) => search,
  loader: async ({ deps }) => {
    const filter = '';
    const qs = new URLSearchParams();
    qs.set('page', String(deps.page));
    qs.set('count', String(deps.count));
    if (deps.sort) qs.set('sort', deps.sort);
    qs.set('isArchived', 'false');
    await Promise.all([
      queryClient.ensureQueryData({
        queryKey: fileStatsKeys.all,
        queryFn: fetchFileStatistics,
        staleTime: 60_000,
      }),
      queryClient.ensureQueryData({
        queryKey: filesListKeys.list({
          page: deps.page,
          count: deps.count,
          sort: deps.sort,
          filter,
        }),
        queryFn: () => fetchFilesList(qs.toString()),
      }),
    ]);
  },
  component: FilesListPage,
});

function FilesListPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const [selectedDocumentId, setSelectedDocumentId] = useState<string | null>(
    null,
  );

  const list = useFilesList(search);
  const { data: stats = [] } = useFileStatistics();
  const {
    data: detail,
    isLoading: isDetailLoading,
    error: detailError,
  } = useFileDetail(selectedDocumentId);
  const { deleteFilesById, isPending: isDeletePending } = useDeleteFiles();
  const { replaceFileById, isPending: isReplacePending } =
    useReplaceFile(selectedDocumentId);

  function handleFiltersChange(filters: Record<string, string[]>) {
    void navigate({ search: prev => ({ ...prev, filters, page: 0 }) });
  }

  function handleQuickFilterChange(quickFilter: string) {
    void navigate({ search: prev => ({ ...prev, quickFilter, page: 0 }) });
  }

  function handlePaginationChange(v: { pageIndex: number; pageSize: number }) {
    void navigate({
      search: prev => ({ ...prev, page: v.pageIndex, count: v.pageSize }),
    });
  }

  async function handleDeleteSelected(ids: string[]) {
    await deleteFilesById(ids);
  }

  async function handleDeleteOne(id: string) {
    await deleteFilesById([id]);
  }

  async function handleReplace(payload: ReplaceFilePayload) {
    await replaceFileById(payload);
  }

  return (
    <div className='flex flex-col flex-1 min-h-0'>
      <FileStatsStrip stats={stats} />

      <div className='flex flex-col flex-1 min-h-0'>
        <FilesTable
          files={list.files}
          isLoading={list.isLoading}
          search={search}
          totalPages={list.totalPages}
          isDeletePending={isDeletePending}
          onSelectFile={setSelectedDocumentId}
          onFiltersChange={handleFiltersChange}
          onQuickFilterChange={handleQuickFilterChange}
          onPaginationChange={handlePaginationChange}
          onDeleteSelected={handleDeleteSelected}
        />
      </div>

      <FileDetailsSheet
        documentId={selectedDocumentId}
        detail={detail}
        isLoading={isDetailLoading}
        isDetailError={!!detailError}
        isDeletePending={isDeletePending}
        isReplacePending={isReplacePending}
        onClose={() => setSelectedDocumentId(null)}
        onDelete={handleDeleteOne}
        onReplace={handleReplace}
      />
    </div>
  );
}
